import apiClient from './apiClient';

/**
 * EVM虚拟机管理服务 (User Version)
 *
 * 处理用户级别的虚拟机管理操作
 * 用户只能管理自己的VM资源
 *
 * 所有路由使用 /api/evm/user/* 前缀
 */
class EvmUserService {
  // ==========================================
  // VM基础管理
  // ==========================================

  /**
   * 获取VM列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.limit - 每页数量
   * @param {string} params.status - 状态筛选 (可选)
   */
  /** Paid orders still waiting for a node (已付款，开通中). */
  async getPendingProvisions() {
    try {
      const response = await apiClient.get('/api/evm/user/pending-provisions');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  async getVmList(params = {}) {
    try {
      const response = await apiClient.get('/api/evm/user/vms', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VM状态
   * @param {number} id - VM ID
   */
  async getState(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/state`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取 VNC WebSocket 连接信息
   * @param {number} id - VM ID
   * @returns {Promise<{ws_url: string, token: string, ts: number, password: string, vm_name: string}>}
   */
  async getNoVnc(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/novnc`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取 Terminal WebSocket 连接信息
   * @param {number} id - VM ID
   * @returns {Promise<{ws_url: string, token: string, ts: number, vm_name: string}>}
   */
  async getTerminal(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/terminal`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VM流量统计
   * @param {number} id - VM ID
   */
  async getTrafficStats(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/traffic`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 电源管理 (7个操作)
  // ==========================================

  /**
   * 电源操作
   * @param {number} id - VM ID
   * @param {string} action - boot|shutdown|reboot|reset|destroy|suspend|resume
   */
  async power(id, action) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/power`, { base: action });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 开机
   */
  async boot(id) {
    return this.power(id, 'boot');
  }

  /**
   * 关机
   */
  async shutdown(id) {
    return this.power(id, 'shutdown');
  }

  /**
   * 重启
   */
  async reboot(id) {
    return this.power(id, 'reboot');
  }

  /**
   * 硬重启
   */
  async reset(id) {
    return this.power(id, 'reset');
  }

  /**
   * 强制关机
   */
  async destroy(id) {
    return this.power(id, 'destroy');
  }

  /**
   * 挂起
   */
  async suspend(id) {
    return this.power(id, 'suspend');
  }

  /**
   * 恢复
   */
  async resume(id) {
    return this.power(id, 'resume');
  }

  // ==========================================
  // 配置管理
  // ==========================================

  /**
   * 设置主机名
   * @param {number} id - VM ID
   * @param {string} hostname - 主机名
   */
  async setHostname(id, hostname) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/hostname`, { hostname });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 设置密码
   * @param {number} id - VM ID
   * @param {string} passwd - 密码
   */
  async setPassword(id, passwd) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/password`, { passwd });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 重建VM
   * @param {number} id - VM ID
   * @param {Object} data - { image_id, passwd? }
   */
  async rebuild(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/rebuild`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用镜像列表 (用于重装)
   */
  async getImages() {
    try {
      const response = await apiClient.get('/api/evm/user/images');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 磁盘管理
  // ==========================================

  /**
   * 获取磁盘列表
   * @param {number} id - VM ID
   */
  async getDisks(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/disks`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加磁盘
   * @param {number} id - VM ID
   * @param {Object} data - { size, init }
   */
  async addDisk(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/disk/add`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新磁盘
   * @param {number} id - VM ID
   * @param {Object} data - { disk_id, size, io, driver, cache, ... }
   */
  async updateDisk(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/disk/update`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除磁盘
   * @param {number} id - VM ID
   * @param {number} disk_id - 磁盘ID
   */
  async deleteDisk(id, disk_id) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/disk/delete`, { disk_id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 快照管理
  // ==========================================

  /**
   * 获取快照列表
   * @param {number} id - VM ID
   */
  async getSnapshots(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}/snapshots`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建快照
   * @param {number} id - VM ID
   * @param {Object} data - { disk_id, description }
   */
  async createSnapshot(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/snapshot`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 恢复快照
   * @param {number} id - VM ID
   * @param {string} snapshot_name - 快照名称
   */
  async recoverSnapshot(id, snapshot_name) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/snapshot/recover`, { snapshot_name });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除快照
   * @param {number} id - VM ID
   * @param {string} snapshot_name - 快照名称
   */
  async deleteSnapshot(id, snapshot_name) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/snapshot/delete`, { snapshot_name });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 备份管理
  // ==========================================

  /**
   * 创建备份
   * @param {number} id - VM ID
   */
  async createBackup(id) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/backup`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // ISO挂载
  // ==========================================

  /**
   * 挂载ISO
   * @param {number} id - VM ID
   * @param {Object} data - { iso_path, dev }
   */
  async mountIso(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/mount`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // IP管理
  // ==========================================

  /**
   * 绑定IP
   * @param {number} id - VM ID
   * @param {Object} data - { ip_id, init }
   */
  async bindIp(id, data) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/ip/add`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 解绑IP
   * @param {number} id - VM ID
   * @param {number} ip_id - IP ID
   */
  async unbindIp(id, ip_id) {
    try {
      const response = await apiClient.post(`/api/evm/user/vms/${id}/ip/delete`, { ip_id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 辅助资源查询
  // ==========================================

  /**
   * 获取可用产品列表
   */
  async getProducts() {
    try {
      const response = await apiClient.get('/api/evm/user/products');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品详情
   * @param {number} id - 产品ID
   */
  async getProduct(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/products/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用镜像模板列表
   */
  async getTemplates() {
    try {
      const response = await apiClient.get('/api/evm/user/templates');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取镜像模板详情
   * @param {number} id - 模板ID
   */
  async getTemplate(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/templates/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品分类列表
   */
  async getProductCategories() {
    try {
      const response = await apiClient.get('/api/evm/user/products/categories');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品的可用模板列表
   * @param {number} productId - 产品ID
   */
  async getProductTemplates(productId) {
    try {
      const response = await apiClient.get(`/api/evm/user/products/${productId}/templates`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品的可用镜像列表
   * @param {number} productId - 产品ID
   */
  async getProductImages(productId) {
    try {
      const response = await apiClient.get(`/api/evm/user/products/${productId}/images`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取产品的可用节点/区域列表
   * @param {number} productId - 产品ID
   */
  async getProductNodes(productId) {
    try {
      const response = await apiClient.get(`/api/evm/user/products/${productId}/nodes`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取 VM 详情
   * @param {number} id - VM ID
   */
  async getVmDetail(id) {
    try {
      const response = await apiClient.get(`/api/evm/user/vms/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // VM 购买
  // ==========================================

  /**
   * 购买/创建 VM
   * @param {Object} data - 购买参数
   * @param {number} data.product_id - 产品ID (必填)
   * @param {number} data.template_id - 配置模板ID (必填)
   * @param {number} data.image_id - 系统镜像ID (必填)
   * @param {number} data.node_id - 节点ID (可选)
   * @param {string} data.hostname - 主机名 (可选)
   * @param {string} data.password - 密码 (可选)
   * @param {number} data.core - CPU核心数 (弹性配置可选)
   * @param {number} data.memory - 内存MB (弹性配置可选)
   * @param {number} data.net_out - 出口带宽Mbps (弹性配置可选)
   * @param {number} data.sys_disk_size - 系统盘GB (弹性配置可选)
   * @param {number} data.data_disk_size - 数据盘GB (弹性配置可选)
   * @param {number} data.public_ip_num - 公网IP数 (弹性配置可选)
   * @param {number} data.snapshot_num - 快照数 (弹性配置可选)
   */
  async purchaseVm(data) {
    try {
      const response = await apiClient.post('/api/evm/user/vms', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // VPC 管理
  // ==========================================

  /**
   * 获取VPC列表
   */
  async getVpcList() {
    try {
      const response = await apiClient.get('/api/user/evm/vpc');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取VPC详情
   */
  async getVpc(id) {
    try {
      const response = await apiClient.get(`/api/user/evm/vpc/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用区域列表
   */
  async getVpcAreas() {
    try {
      const response = await apiClient.get('/api/user/evm/vpc/areas');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可用区列表
   */
  async getVpcZones(areaId) {
    try {
      const response = await apiClient.get('/api/user/evm/vpc/zones', { params: { area_id: areaId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建VPC
   */
  async createVpc(data) {
    try {
      const response = await apiClient.post('/api/user/evm/vpc', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 更新VPC
   */
  async updateVpc(id, data) {
    try {
      const response = await apiClient.post(`/api/user/evm/vpc/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除VPC
   */
  async deleteVpc(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/vpc/${id}/delete`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 添加VPC子网
   */
  async addVpcSubnet(vpcId, data) {
    try {
      const response = await apiClient.post(`/api/user/evm/vpc/${vpcId}/subnet`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除VPC子网
   */
  async deleteVpcSubnet(vpcId, subnetId) {
    try {
      const response = await apiClient.post(`/api/user/evm/vpc/${vpcId}/subnet/${subnetId}/delete`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // EIP 管理
  // ==========================================

  /**
   * 获取EIP列表
   */
  async getEipList() {
    try {
      const response = await apiClient.get('/api/user/evm/eip');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 分配EIP
   */
  async allocateEip(data) {
    try {
      const response = await apiClient.post('/api/user/evm/eip/allocate', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 释放EIP
   */
  async releaseEip(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/eip/${id}/release`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 绑定EIP
   */
  async bindEip(data) {
    try {
      const response = await apiClient.post('/api/user/evm/eip/bind', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 解绑EIP
   */
  async unbindEip(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/eip/${id}/unbind`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 获取可绑定VM列表
   */
  async getBindableVms(eipId) {
    try {
      const response = await apiClient.get(`/api/user/evm/eip/${eipId}/bindable-vms`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  // ==========================================
  // 安全组管理
  // ==========================================

  /**
   * 获取安全组列表
   */
  async getSecurityGroupList() {
    try {
      const response = await apiClient.get('/api/user/evm/security-group');
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 获取安全组详情
   */
  async getSecurityGroup(id) {
    try {
      const response = await apiClient.get(`/api/user/evm/security-group/${id}`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 创建安全组
   */
  async createSecurityGroup(data) {
    try {
      const response = await apiClient.post('/api/user/evm/security-group', data);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 更新安全组
   */
  async updateSecurityGroup(id, data) {
    try {
      const response = await apiClient.post(`/api/user/evm/security-group/${id}`, data);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 删除安全组
   */
  async deleteSecurityGroup(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/security-group/${id}/delete`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 添加安全组规则
   */
  async addSecurityGroupRule(groupId, data) {
    try {
      const response = await apiClient.post(`/api/user/evm/security-group/${groupId}/rule`, data);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 删除安全组规则
   */
  async deleteSecurityGroupRule(groupId, ruleId) {
    try {
      const response = await apiClient.post(`/api/user/evm/security-group/${groupId}/rule/${ruleId}/delete`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  /**
   * 批量更新安全组规则
   */
  async updateSecurityGroupRules(groupId, rules) {
    try {
      const response = await apiClient.post(`/api/user/evm/security-group/${groupId}/rules`, { rules });
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.msg || error.message;
      return { success: false, status: 'error', msg, error: msg };
    }
  }

  // ==========================================
  // 端口转发管理
  // ==========================================

  /**
   * 获取端口转发列表
   */
  async getPortForwardList(params = {}) {
    try {
      const response = await apiClient.get('/api/user/evm/port-forward', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 创建端口转发
   */
  async createPortForward(data) {
    try {
      const response = await apiClient.post('/api/user/evm/port-forward', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /**
   * 删除端口转发
   */
  async deletePortForward(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/port-forward/${id}/delete`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.message };
    }
  }

  /** NAT websites: domain -> VM :80 (Host) / :443 (SNI passthrough) */
  async createSiteForward(data) {
    try {
      const response = await apiClient.post('/api/user/evm/site-forward', data);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  async deleteSiteForward(id) {
    try {
      const response = await apiClient.post(`/api/user/evm/site-forward/${id}/delete`);
      return response.data;
    } catch (error) {
      return { status: 'error', msg: error.response?.data?.msg || error.message };
    }
  }

  // ---- 2026-10 infra batch (network / metrics / backups) ----
  async _get(path, params) {
    try {
      const response = await apiClient.get(path, { params });
      return response.data;
    } catch (error) {
      return { success: false, status: 'error', error: error.response?.data?.msg || error.message };
    }
  }

  async _post(path, data) {
    try {
      const response = await apiClient.post(path, data);
      return response.data;
    } catch (error) {
      return { success: false, status: 'error', error: error.response?.data?.msg || error.message };
    }
  }

  getVmNetwork(id) { return this._get(`/api/evm/user/vms/${id}/network`); }
  attachVpc(id, subnetId, restart = false, confirmCross = false) { return this._post(`/api/evm/user/vms/${id}/vpc/attach`, { subnet_id: subnetId, restart, confirm_cross: confirmCross ? 1 : 0 }); }
  detachVpc(id, portId, restart = false) { return this._post(`/api/evm/user/vms/${id}/vpc/detach`, { port_id: portId, restart }); }
  setVmSecurityGroups(id, ids) { return this._post(`/api/evm/user/vms/${id}/security-groups`, { ids }); }
  getVmMetrics(id, range = '1h') { return this._get(`/api/evm/user/vms/${id}/metrics`, { range }); }
  getVmTrafficSeries(id, granularity = 'hour') { return this._get(`/api/evm/user/vms/${id}/traffic-series`, { granularity }); }
  vmRescue(id, action) { return this._post(`/api/evm/user/vms/${id}/rescue`, { action }); }
  getVmRdns(id) { return this._get(`/api/evm/user/vms/${id}/rdns`); }
  getSnapshotSchedule(id) { return this._get(`/api/evm/user/vms/${id}/snapshot-schedule`); }
  getAlertRules(vmId) { return this._get('/api/evm/user/alert-rules', vmId ? { vm_id: vmId } : undefined); }
  createAlertRule(data) { return this._post('/api/evm/user/alert-rules', data); }
  updateAlertRule(id, data) { return this._post(`/api/evm/user/alert-rules/${id}`, data); }
  deleteAlertRule(id) { return this._post(`/api/evm/user/alert-rules/${id}/delete`, {}); }
  saveSnapshotSchedule(id, data) { return this._post(`/api/evm/user/vms/${id}/snapshot-schedule`, data); }
  setVmRdns(id, data) { return this._post(`/api/evm/user/vms/${id}/rdns`, data); }
  getVmBackups(id) { return this._get(`/api/evm/user/vms/${id}/backups`); }
  restoreVmBackup(id, backupId) { return this._post(`/api/evm/user/vms/${id}/backup/restore`, { backup_id: backupId }); }
  deleteVmBackup(id, backupId) { return this._post(`/api/evm/user/vms/${id}/backup/delete`, { backup_id: backupId }); }
  // scheduled encrypted backups (restic)
  getBackupPlan(id) { return this._get(`/api/evm/user/vms/${id}/backup-plan`); }
  getBackupPoints(id) { return this._get(`/api/evm/user/vms/${id}/backup-points`); }
  runBackupNow(id) { return this._post(`/api/evm/user/vms/${id}/backup-points/run`, {}); }
  restoreBackupPoint(id, pid, body = {}) { return this._post(`/api/evm/user/vms/${id}/backup-points/${pid}/restore`, { dev: body.dev || '', power_on: body.power_on || 'keep' }); }
  deleteBackupPoint(id, pid) { return this._post(`/api/evm/user/vms/${id}/backup-points/${pid}/delete`, {}); }
  getBackupAddon(id) { return this._get(`/api/evm/user/vms/${id}/backup-addon`); }
  buyBackupAddon(id, points) { return this._post(`/api/evm/user/vms/${id}/backup-addon`, { points }); }
  cancelBackupAddon(id) { return this._post(`/api/evm/user/vms/${id}/backup-addon/cancel`, {}); }

  // ---- renewal / expiry ----
  getVmBilling(id) { return this._get(`/api/evm/user/vms/${id}/billing`); }
  setVmAutoRenew(id, enabled) { return this._post(`/api/evm/user/vms/${id}/auto-renew`, { enabled: enabled ? 1 : 0 }); }
  renewVm(id, months) { return this._post(`/api/evm/user/vms/${id}/renew`, { months }); }
  /** Monthly traffic-fee cap of this VM (null = platform cap). */
  setVmTrafficCap(id, cap) { return this._post(`/api/evm/user/vms/${id}/traffic-cap`, { cap }); }
  /** Hourly VM: delete now; unused whole hours go back to balance. confirm = hostname/name. */
  releaseVm(id, confirm) { return this._post(`/api/evm/user/vms/${id}/release`, { confirm }); }

  // ---- traffic packs (current period) ----
  getTrafficPack(id, qty = 1) { return this._get(`/api/evm/user/vms/${id}/traffic-pack`, { qty }); }
  buyTrafficPack(id, qty = 1) { return this._post(`/api/evm/user/vms/${id}/traffic-pack`, { qty }); }

  // ---- SSH keys bound to the VM ----
  getVmSshKeys(id) { return this._get(`/api/evm/user/vms/${id}/ssh-keys`); }
  bindVmSshKeys(id, keyIds) { return this._post(`/api/evm/user/vms/${id}/ssh-keys/bind`, { key_ids: keyIds }); }
  unbindVmSshKeys(id, ids) { return this._post(`/api/evm/user/vms/${id}/ssh-keys/unbind`, { ids }); }
  setVmSshKeyOnly(id, enabled) { return this._post(`/api/evm/user/vms/${id}/ssh-keys/key-only`, { enabled: !!enabled }); }
}

export default new EvmUserService();
